import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { iaApi, errMsg, openBlob } from './api';
import { SessionsSidebar } from './SessionsSidebar';
import { UploadCard } from './UploadCard';
import { MissingDataCard } from './MissingDataCard';
import { ExtractedDataCard } from './ExtractedDataCard';
import { DevisCard } from './DevisCard';
import { DevisDashboard } from './DevisDashboard';
import { ClassementCoopers } from './ClassementCoopers';
import { ImpayesTable } from '../invitation/ImpayesTable';

export default function DOMIUMAssistantDevis() {
  const { getAuthHeader, user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(String(user?.role || '').toLowerCase());
  const api = useMemo(() => iaApi(getAuthHeader), [getAuthHeader]);
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(null);

  const [stats, setStats] = useState(null);
  const refreshList = useCallback(() => {
    api.dashboard().then(setStats).catch(() => setStats(null));
    return api.list().then(setSessions).catch(() => toast.error('Sessions indisponibles'));
  }, [api]);
  useEffect(() => { refreshList(); }, [refreshList]);

  const exportExcel = async () => {
    setBusy('export');
    try {
      const url = URL.createObjectURL(await api.exportXlsx(''));
      Object.assign(document.createElement('a'), { href: url, download: `devis_ia_${new Date().toISOString().slice(0, 10)}.xlsx` }).click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Export impossible"); } finally { setBusy(null); }
  };

  const select = async (id) => {
    setBusy('load');
    let s = null;
    try { s = await api.get(id); setCurrent(s); } catch (e) { toast.error(errMsg(e, 'Session introuvable')); } finally { setBusy(null); }
    if (s?.analyse_job?.status === 'en_cours') suivreAnalyse(s).catch(() => {});
  };
  // L'analyse tourne en arrière-plan côté serveur : on suit son état toutes les 5 s (max 15 min)
  const suivreAnalyse = async (s) => {
    setBusy('analyse');
    try {
      for (let i = 0; i < 180 && s?.analyse_job?.status === 'en_cours'; i += 1) {
        await new Promise((r) => setTimeout(r, 5000));
        s = await api.get(s.id);
      }
      setCurrent(s);
      refreshList();
      if (s?.analyse_job?.status === 'erreur') throw new Error(s.analyse_job.detail || "L'analyse a échoué");
      if (s?.analyse_job?.status === 'en_cours') throw new Error("L'analyse prend plus de temps que prévu : rouvrez la session dans quelques minutes");
      return s;
    } finally { setBusy(null); }
  };
  const create = async () => {
    const s = await api.create();
    await refreshList();
    select(s.id);
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cette session ?')) return;
    await api.remove(id);
    if (current?.id === id) setCurrent(null);
    refreshList();
  };
  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const s = await fn(); setCurrent(s); refreshList(); if (okMsg) toast.success(okMsg); return true; }
    catch (e) { toast.error(errMsg(e, 'Une erreur est survenue')); return false; }
    finally { setBusy(null); }
  };
  const lancerAnalyse = async (fn) => {
    setBusy('analyse');
    try {
      const s = await fn();
      setCurrent(s);
      toast.info("Analyse IA lancée — elle peut prendre 2 à 3 minutes, vous pouvez rester sur cette page");
      await suivreAnalyse(s);
      toast.success('Analyse IA terminée');
      return true;
    } catch (e) {
      toast.error(e?.response ? errMsg(e, 'Une erreur est survenue') : e.message);
      setBusy(null);
      return false;
    }
  };
  const analyse = async (files) => {
    let s = current;
    if (!s) { s = await api.create(); }
    return lancerAnalyse(() => api.analyse(s.id, files));
  };
  // Pack prêt d'avance : régénéré dès que le taux ou le montant change
  const [prepTick, setPrepTick] = useState(0);
  const saveEtPreparer = async (body, msg) => { if (await run('save', () => api.save(current.id, body), msg)) setPrepTick((t) => t + 1); };
  const onVersion = async (sid, action, v) => {
    try {
      if (action === 'ouvrir') { openBlob(await api.versionPdf(sid, v.id), 'application/pdf'); return; }
      if (action === 'definitif' && !window.confirm(`Marquer la V${v.numero} comme souscription définitive ? Les autres versions seront archivées et le taux et le montant seront verrouillés.`)) return;
      if (action === 'retirer-definitif' && !window.confirm(`Retirer le définitif de la V${v.numero} ? Le taux et le montant redeviendront modifiables.`)) return;
      const s = await api.versionAction(sid, v.id, action);
      await refreshList();
      if (action === 'recharger') { setCurrent(s); setPrepTick((t) => t + 1); toast.success(`Taux et montant de la V${v.numero} rechargés`); return; }
      if (current?.id === sid) setCurrent(s);
      toast.success({ definitif: `V${v.numero} marquée définitive — taux et montant verrouillés`, 'retirer-definitif': `V${v.numero} n'est plus définitive — dossier déverrouillé` }[action] || 'Version mise à jour');
    } catch (e) { toast.error(errMsg(e, 'Action impossible sur cette version')); }
  };

  return (
    <div className="space-y-4 mb-4" data-testid="domium-assistant-devis">
    <div className="flex justify-end gap-2">
      {isAdmin && <ImpayesTable getAuthHeader={getAuthHeader} defaultProduit="domium" />}
      <Button variant="outline" size="sm" onClick={exportExcel} disabled={busy === 'export'} className="gap-1.5 border-emerald-300 text-emerald-700" data-testid="ia-export-excel-btn">
        {busy === 'export' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Exporter Excel
      </Button>
    </div>
    <DevisDashboard stats={stats} />
    {isAdmin && <ClassementCoopers api={api} refreshKey={sessions.length} />}
    <div className="grid lg:grid-cols-[280px_1fr] gap-4">
      <SessionsSidebar sessions={sessions} currentId={current?.id} onSelect={select} onCreate={create} onDelete={remove} onVersion={onVersion} />
      <div className="space-y-4 min-w-0">
        {busy === 'load' && <Loader2 className="h-5 w-5 animate-spin text-violet-500" />}
        <UploadCard
          session={current} api={api} analysing={busy === 'analyse'} onAnalyse={analyse}
          onReanalyse={() => window.confirm('Ré-analyser toutes les pièces ? Les données extraites seront remplacées.') && lancerAnalyse(() => api.reanalyse(current.id))}
          onChanged={(s) => { setCurrent(s); refreshList(); }}
        />
        {current?.form && (
          <>
            <MissingDataCard key={`m-${current.id}`} session={current} api={api} onChanged={() => select(current.id)} />
            <ExtractedDataCard session={current} saving={busy === 'save'} onSave={(body) => run('save', () => api.save(current.id, body), 'Données enregistrées, contribution recalculée')} />
            <DevisCard
              session={current} api={api} getAuthHeader={getAuthHeader} busy={busy === 'generate' || busy === 'save'} prepTick={prepTick}
              onSessionUpdate={(s) => { setCurrent(s); refreshList(); }}
              onSaveOverride={(v) => saveEtPreparer({ contribution_override_ht: v }, v ? 'Montant rectifié' : 'Montant calculé rétabli')}
              onSaveTaux={(v) => saveEtPreparer({ taux_override: v }, v ? 'Taux rectifié' : 'Taux calculé rétabli')}
              onGenerate={() => run('generate', () => api.generate(current.id), 'Pack Devis ESS + CP/CG généré')}
            />
          </>
        )}
      </div>
    </div>
    </div>
  );
}
